<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { formatDateLocale } from '@/libs/utils/date.js'

import Paginazione from '@/components/Generics/Paginations/Paginazione.vue'
import ModalLog from '@/components/Doctrine/includes/ModalLog.vue'

import { client } from '@/services/fetch.js'
import { usePageNumberStore } from '@/services/store.js'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faArrowsTurnToDots, faEllipsisVertical, faEye, faKey, faPencil, faTrash } from '@digitalpa/pro-solid-svg-icons'

library.add(
  faPencil,
  faEye,
  faEllipsisVertical,
  faKey,
  faArrowsTurnToDots,
  faTrash,
)

const pageNumberStore = usePageNumberStore()
const route = useRoute();
const isLoading = ref(true)

const logs = ref({
  data: [],
  pagination: {},
  filters: []
})

const loading = ref(true);
const error = ref(null);
const showModal = ref(false);
const logDetails = ref(null);
const modalLoading = ref(false);
const filters = ref([]);

// Filtri selezionati
const selectedOperation = ref('');
const selectedEntity = ref('');
const selectedUser = ref('');

const timeFrom = ref('');
const timeTo = ref('');

// Fetch dei dati
const fetchLogs = async (page) => {
  try {
    let apiUrl = '/doctrine-log/api/event-log';
    const queryParams = new URLSearchParams();
    if (timeFrom.value && timeTo.value) {
      if (timeFrom.value > timeTo.value) {
        alert('La data "Da" (timeFrom) deve essere inferiore alla data "A" (timeTo).');
        timeFrom.value = timeTo.value;
        return; // Non inviare la richiesta
      }
    }
    if (selectedEntity.value) queryParams.append('table', selectedEntity.value.replace(/^App\\Entity\\/, ''));
    if (selectedUser.value) queryParams.append('user', selectedUser.value);
    if (selectedOperation.value) queryParams.append('operation', selectedOperation.value);
    if (timeFrom.value)
    {
      const timeFromToSend = new Date(timeFrom.value);
      queryParams.append('timeFrom', timeFromToSend.toISOString());
    }
    if (timeTo.value)
    {
      const timeToSend = new Date(timeTo.value);
      queryParams.append('timeTo', timeToSend.toISOString());
    }
    queryParams.append('page', isNaN(page) || page <= 0 ? 1 : page);
    if ([...queryParams].length > 0) {
      apiUrl += '?' + queryParams.toString();
    }

    const response = await client.get(apiUrl);
    logs.value.data = response.data || [];
    logs.value.filters = response.filters;
    logs.value.pagination = response.pagination;
    logs.value.users = response.users;
    if (logs.value.filters) {
      uniqueEntities.value = logs.value.filters.entities ? [...new Set(Object.values(logs.value.filters.entities))] : [];
      uniqueUsers.value = logs.value.filters.users ? [...new Set(Object.values(logs.value.filters.users))] : [];
      uniqueOperations.value = logs.value.filters.operations ? [...new Set(Object.values(logs.value.filters.operations))] : [];
    }
  } catch (err) {
    error.value = err.message;

  } finally {
    loading.value = false;
  }

};
onMounted(async () => {
  await fetchLogs(pageNumberStore.getPage(route.name))
})

const uniqueEntities = ref([]);
const uniqueUsers = ref([]);
const uniqueOperations = ref([]);

const sortedAndFilteredLogs = computed(() => {
  return [...logs.value.data].sort((a, b) => b.id - a.id);
});

const changePage = async (page) => {
  loading.value = true
  await fetchLogs(page)
  loading.value = false
}

const viewDetails = async (id) => {
  const apiUrl = '/doctrine-log/api/event-log/'+id;
  modalLoading.value = true;
  logDetails.value = null; // Resetta i dati prima di caricare
  showModal.value = true;
  try {
    logDetails.value = await client.get(apiUrl); // Ora assegni l'oggetto corretto

  }catch (err) {
    error.value = err.message;
  } finally {
    modalLoading.value = false;
  }

};

const closeModal = () => {
  showModal.value = false;
  logDetails.value = null; // Reset dei dettagli
};
</script>

<template>
  <div>
    <div class="container-fluid ps-0 pe-0" >
      <div class="row g-2">
        <div class="col-md">
          <label>Entity:</label>
          <select class="form-control" v-model="selectedEntity" @change="changePage">
            <option value="">Tutti</option>
            <option v-for="ent in uniqueEntities" :key="ent" :value="ent">{{ ent }}</option>
          </select>
        </div>

        <div class="col-md">
          <label>Operazione:</label>
          <select class="form-control" v-model="selectedOperation" @change="changePage">
            <option value="">Tutti</option>
            <option v-for="op in uniqueOperations" :key="op" :value="op">{{ op }}</option>
          </select>
        </div>

        <div class="col-md">
          <label>User:</label>
          <select class="form-control" v-model="selectedUser" @change="changePage">
            <option value="">Tutti</option>
            <option v-for="user in uniqueUsers" :key="user" :value="user">{{ logs.users.usernames[user].username +' ('+user+')' }}</option>
          </select>
        </div>

        <div class="col-md">
          <label>Da:</label>
          <input class="form-control" type="datetime-local" v-model="timeFrom" @change="changePage">
        </div>

        <div class="col-md">
          <label>A:</label>
          <input class="form-control" type="datetime-local" v-model="timeTo" @change="changePage">
        </div>

        <div class="col-md d-flex align-items-end">
          <button class="btn btn-primary  w-100" @click="changePage">Cerca</button>
        </div>
      </div>
    </div>



    <div
      v-if="loading"
      class="d-flex justify-content-center "
    >
      <div class="spinner-grow " role="status">
        <span class="sr-only">Loading...</span>
      </div>

    </div>
    <div v-if="logs.data.length === 0 && !loading" class="alert alert-warning mt-2" role="alert">
      Nessun log disponibile.
    </div>
    <div class="table-responsive mt-1 list-f" ref="tableList">
    <table class="table table-striped mb-0" v-if="!loading && logs.data.length > 0">
      <thead>
      <tr>
        <th>Entity</th>
        <th>Entry ID</th>
        <th>Username</th>
        <th>Operazione</th>
        <th>Request Path</th>
        <th>Orario</th>
        <th style="text-align: center">Azioni</th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="log in sortedAndFilteredLogs" :key="log.id">
        <td>{{ log.entityTable }}</td>
        <td>{{ log.entryId }}</td>
        <td>{{ logs.users.usernames[log.userReference].username }}</td>
        <td>{{ log.iOoperation }}</td>
        <td>{{ log.requestPath }}</td>
        <td>{{ formatDateLocale(log.time) }}</td>
        <td class="text-center align-middle">
          <button @click="viewDetails(log.id)"  class="btn btn-primary" style="margin-left: auto">
            <span><i class="fas fa-eye"></i> Mostra</span>
          </button>
        </td>
      </tr>
      </tbody>
    </table>
      <div
        v-if="
              Math.ceil(
                parseInt(logs.pagination.total_items) /
                  parseInt(logs.data.length),
              ) > 1
            "
        class="d-flex justify-content-end m-2 mt-4"
      >
        <Paginazione
          :total-pages="
                Math.ceil(
                  logs.pagination.total_items /
                    logs.pagination.items_per_page,
                )
              "
          @page-changed="changePage"
        />
      </div>
    </div>
  </div>

  <ModalLog
    :show="showModal"
    :logDetails="logDetails"
    :loading="modalLoading"
    :logs="logs"
    @close="closeModal"
  />

</template>

<style scoped>
table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

thead {
  background-color: white;
  border-bottom: 1px solid black;
}

th, td {
  padding: 8px;
  text-align: left;
}

td {
  font-size:14px;
}

tbody tr:hover td {
  color: #333; /* Testo più scuro */
  background-color: rgba(255, 255, 0, 0.42); /* Grigio più scuro */
}

.filters {
  display: flex;
  gap: 15px;
  margin-bottom: 10px;
}

select {
  padding: 5px;
  font-size: 14px;
}
.time{
  padding: 5px;
  font-size: 14px;
}

input.form-control[type="datetime-local"] {
  padding-top: 4px;
  padding-bottom: 4px;
}
</style>
